import { useState } from "react";
import { Send, Phone, Instagram } from "lucide-react";
import { toast } from "sonner";
import { CONTACT_EMAIL, PHONE, PHONE_TEL } from "@/lib/data";

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "Prise de contact", message: "" });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Merci de remplir tous les champs requis.");
      return;
    }
    const body = `Nom : ${form.name}%0AEmail : ${form.email}%0A%0A${encodeURIComponent(form.message)}`;
    const url = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(form.subject)}&body=${body}`;
    window.location.href = url;
    toast.success("Votre client mail s'ouvre. Merci !");
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#C4F4E4]/60" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <span className="font-sans text-sm font-bold tracking-[0.25em] uppercase text-[#FF6B6B]">
            Contact
          </span>
          <h2 className="mt-3 font-display text-5xl md:text-7xl leading-none uppercase">
            Écrivez-nous<span className="text-[#8B5CF6]">.</span>
          </h2>
          <p className="mt-5 text-lg text-[#1a1a1a] max-w-md">
            Une question, une envie, un projet ? On vous répond avec plaisir.
          </p>

          <div className="mt-8 space-y-3">
            <a
              href={`tel:${PHONE_TEL}`}
              className="flex items-center gap-3 bg-white border-2 border-black rounded-2xl px-5 py-4 shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
              data-testid="contact-phone-link"
            >
              <Phone size={20} />
              <div>
                <p className="text-xs uppercase tracking-wider font-bold text-[#3a3a3a]">Téléphone</p>
                <p className="font-display text-2xl">{PHONE}</p>
              </div>
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="flex items-center gap-3 bg-white border-2 border-black rounded-2xl px-5 py-4 shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
              data-testid="contact-email-link"
            >
              <Send size={20} />
              <div>
                <p className="text-xs uppercase tracking-wider font-bold text-[#3a3a3a]">Email</p>
                <p className="font-sans font-bold break-all">{CONTACT_EMAIL}</p>
              </div>
            </a>
            <div className="flex gap-3">
              <a
                href="https://instagram.com/atelierloraineb"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-atelierloraineb"
                className="flex-1 flex items-center gap-2 bg-[#FFD1DC] border-2 border-black rounded-2xl px-4 py-3 font-sans font-bold text-sm shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
              >
                <Instagram size={18} /> @atelierloraineb
              </a>
              <a
                href="https://instagram.com/low.rainboww"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="instagram-lowrainboww"
                className="flex-1 flex items-center gap-2 bg-[#E6D7FF] border-2 border-black rounded-2xl px-4 py-3 font-sans font-bold text-sm shadow-brutal-sm hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-none transition-all"
              >
                <Instagram size={18} /> @low.rainboww
              </a>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-white border-2 border-black rounded-3xl p-7 md:p-10 shadow-brutal"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <div>
              <label className="block font-sans text-xs font-bold tracking-widest uppercase mb-2">Nom *</label>
              <input
                type="text"
                name="name"
                required
                value={form.name}
                onChange={onChange}
                data-testid="contact-input-name"
                className="w-full bg-[#FAFAFA] border-2 border-black rounded-xl px-4 py-3 font-sans focus:outline-none focus:bg-[#FFF3B0]"
              />
            </div>
            <div>
              <label className="block font-sans text-xs font-bold tracking-widest uppercase mb-2">Email *</label>
              <input
                type="email"
                name="email"
                required
                value={form.email}
                onChange={onChange}
                data-testid="contact-input-email"
                className="w-full bg-[#FAFAFA] border-2 border-black rounded-xl px-4 py-3 font-sans focus:outline-none focus:bg-[#FFF3B0]"
              />
            </div>
          </div>
          <div className="mt-5">
            <label className="block font-sans text-xs font-bold tracking-widest uppercase mb-2">Sujet</label>
            <select
              name="subject"
              value={form.subject}
              onChange={onChange}
              data-testid="contact-input-subject"
              className="w-full bg-[#FAFAFA] border-2 border-black rounded-xl px-4 py-3 font-sans focus:outline-none"
            >
              <option>Prise de contact</option>
              <option>Demande de devis - Atelier scolaire</option>
              <option>Demande de devis - Entreprise / Particulier</option>
              <option>Infos & programmation - Spectacle</option>
              <option>Coaching personnalisé</option>
            </select>
          </div>
          <div className="mt-5">
            <label className="block font-sans text-xs font-bold tracking-widest uppercase mb-2">Message *</label>
            <textarea
              name="message"
              rows={6}
              required
              value={form.message}
              onChange={onChange}
              data-testid="contact-input-message"
              className="w-full bg-[#FAFAFA] border-2 border-black rounded-xl px-4 py-3 font-sans focus:outline-none focus:bg-[#FFF3B0] resize-none"
            />
          </div>
          <button
            type="submit"
            data-testid="contact-submit"
            className="mt-7 w-full md:w-auto bg-black text-white rounded-full px-8 py-4 font-sans font-bold inline-flex items-center justify-center gap-2 hover:-translate-y-1 transition-transform"
          >
            <Send size={18} /> Envoyer le message
          </button>
        </form>
      </div>
    </section>
  );
};
